import { useHasAccess } from "../hooks/useHasAccess";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { pluralize } from "../lib/inflection";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter } from "./ModalCard";
import { Text } from "./Text";
import { WorkflowPreviewList } from "./WorkflowPreviewList";

export function DeleteSpaceModal({
  spaceId,
  spaceName,
  onClose,
  onDeleted,
}: {
  spaceId: string;
  spaceName: string;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const z = useZero();
  const canManage = useHasAccess("space:manage", spaceId);
  const [workflows, workflowsResult] = useQuery(
    queries.spaceDeletionPreviewWorkflows({ spaceId })
  );
  const loading = workflowsResult.type !== "complete";
  const canDelete = canManage && !loading && workflows.length === 0;

  return (
    <Modal open onClose={onClose} alert>
      <ModalContent style={{ width: 320 }}>
        <Text variant="text-regular-md" color="var(--text-primary)">
          Delete space
        </Text>
        {loading ? (
          <Text variant="text-regular" color="var(--text-secondary)">
            Loading workflows…
          </Text>
        ) : workflows.length === 0 ? (
          <Text variant="text-regular" color="var(--text-secondary)">
            Permanently deletes “{spaceName}” and everything in it — workflows,
            steps, and runs.
          </Text>
        ) : (
          <Text variant="text-regular" color="var(--text-secondary)">
            Move or delete the {workflows.length}{" "}
            {pluralize("workflow", workflows.length)} in “{spaceName}” before
            deleting this space.
          </Text>
        )}
        {!loading && <WorkflowPreviewList workflows={workflows} />}
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="l"
            variant="danger"
            disabled={!canDelete}
            onClick={() => {
              if (!canDelete) return;
              z.mutate(sharedMutators.deleteSpace({ spaceId }));
              onDeleted?.();
              onClose();
            }}
          >
            Delete space
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
